import React, {useRef, useState, useId} from 'react'
import PropTypes from 'prop-types'
import {Box, Text, useStyleConfig} from '@chakra-ui/react'
import {Navigation, Pagination, Keyboard} from 'swiper/modules'
import {Swiper, SwiperSlide} from 'swiper/react'
import PromoSlideImageCard from './partials/promo-slide-image-card'
import {useIntl} from 'react-intl'
import {remCalc} from '../../../theme/_utils'
import PromoSliderIcons from './promo-slider-icon-arrow'
import {HideOnMobile} from '@salesforce/retail-react-app/app/components/responsive'

export const PromoSliderImage = ({heading, sliderGroup, uid}) => {
    const generatedId = useId().replace(/:/g, '')
    const sliderId = uid || generatedId
    const swiperStyles = useStyleConfig('Swiper')
    const styles = useStyleConfig('PromoSlider')
    const swiperRef = useRef(null)
    const prevButtonRef = useRef(null)
    const nextButtonRef = useRef(null)
    const navigationDirection = useRef(null)
    const intl = useIntl()

    const [srAnnouncement, setSrAnnouncement] = useState('')

    const slidesToView = {base: 1, md: 1.5, lg: 2.75, xl: 2.75, xxl: 2.75}

    if (!sliderGroup || sliderGroup.length === 0) return null

    // swiperjs doesn't work correctly with 3 slides when loop + centeredSlides are enabled
    const slides = [...sliderGroup]
    const needsFakeSlides = slides.length === 3
    const allSlides = needsFakeSlides
        ? [...slides, ...slides.map((s) => ({...s, isFake: true}))]
        : slides

    const setBulletA11yState = (realIndex) => {
        const bullets = document.querySelectorAll(
            `#promo-slider-image-${sliderId} .swiper-pagination-bullet`,
        )
        bullets.forEach((bullet, idx) => {
            if (idx === realIndex) bullet.setAttribute('aria-current', 'true')
            else bullet.removeAttribute('aria-current')
        })
    }

    const announceSlide = (realIndex) => {
        const title = slides?.[realIndex]?.text_block?.title
        const paragraph = slides?.[realIndex]?.text_block.body_copy
        const imgDesc = slides?.[realIndex]?.media?.description
        const label = `${title} ${paragraph}` || imgDesc
        setSrAnnouncement(`${label ? `: ${label}` : ''}`)
    }

    return (
        <Box
            id={`promo-slider-image-${sliderId}`}
            position="relative"
            __css={{...swiperStyles, ...styles.container}}
            data-testid="promo-slider-image-container"
        >
            {/*This is used so the screen reader will readout the new image selected on the swiper without stealing focus from the selected controll (prev / next arrows) */}
            <Box srOnly role="status" aria-live="polite" aria-atomic="true">
                {srAnnouncement}
            </Box>

            {heading && (
                <Text as="h2" {...styles.heading}>
                    {heading}
                </Text>
            )}
            <Box position="relative">
                {/* note chevronIcon is used all swipers thats why top being overwritten*/}
                <HideOnMobile>
                    <Box
                        ref={prevButtonRef}
                        className={`swiper-button-prev swiper-button-prev-${sliderId}`}
                        aria-label={intl.formatMessage({
                            id: 'label.promo.swiper.button.prev',
                            defaultMessage: 'swiper button previous icon',
                        })}
                        tabIndex={0}
                        role="button"
                        onClick={() => {
                            navigationDirection.current = 'prev'
                            swiperRef.current?.slidePrev()
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault()
                                e.stopPropagation()
                                navigationDirection.current = 'prev'
                                if (swiperRef.current) {
                                    swiperRef.current.slidePrev()
                                }
                            }
                        }}
                        {...styles.heroLeftRightArrowsContainer}
                        top={{base: '50%', md: 'calc(50% - 60px)', lg: '50%'}}
                        left={{base: '4%', md: '18%', lg: '22%', xl: '26%'}}
                    >
                        <PromoSliderIcons.left
                            {...styles.heroLeftRightArrows}
                            boxSize={remCalc(16)}
                        />
                    </Box>
                </HideOnMobile>
                <Swiper
                    aria-label={intl.formatMessage({
                        id: 'label.promo.swiper.slider',
                        defaultMessage: 'Swiper',
                    })}
                    modules={[Navigation, Pagination, Keyboard]}
                    navigation={{
                        prevEl: `.swiper-button-prev-${sliderId}`,
                        nextEl: `.swiper-button-next-${sliderId}`,
                    }}
                    pagination={{
                        clickable: true,
                        el: `#promo-slider-image-${sliderId} .promo-slider-pagination`,
                        renderBullet: (index, className) => {
                            if (index < slides.length) {
                                const changeSlideLabel = intl.formatMessage({
                                    id: 'label.promo.swiper.slider.image',
                                    defaultMessage: 'Change slide',
                                })
                                return `<button type="button" class="${className}" aria-label="${changeSlideLabel}" aria-controls="promo-slider-image-${sliderId}" data-slide-index="${index}"></button>`
                            }
                            return ''
                        },
                    }}
                    keyboard={{
                        enabled: true,
                        onlyInViewport: false,
                        pageUpDown: false,
                    }}
                    loop={true}
                    centeredSlides={true}
                    tabIndex={-1}
                    onInit={(swiper) => {
                        swiperRef.current = swiper
                        const bullets = document.querySelectorAll(
                            `#promo-slider-image-${sliderId} .swiper-pagination-bullet`,
                        )
                        bullets.forEach((b) => b.setAttribute('tabindex', '-1'))
                        const initialRealIndex = swiper.realIndex % slides.length
                        setBulletA11yState(initialRealIndex)
                        announceSlide(initialRealIndex)

                        // Set all slide links to tabindex -1 first
                        const allSlideLinks = document.querySelectorAll(
                            `#promo-slider-image-${sliderId} .swiper-slide a`,
                        )
                        allSlideLinks.forEach((link) => {
                            link.setAttribute('tabindex', '-1')
                        })

                        // Then set only the active slide's link to 0
                        const activeSlide = swiper.slides[swiper.activeIndex]
                        if (activeSlide) {
                            const activeLink = activeSlide.querySelector('a')
                            if (activeLink) {
                                activeLink.setAttribute('tabindex', '0')
                            }
                        }
                    }}
                    onSlideChange={(swiper) => {
                        const realIndex = swiper.realIndex % slides.length
                        setBulletA11yState(realIndex)
                        announceSlide(realIndex)

                        // Update tabindex for all slides - set all to -1 first
                        const allSlideLinks = document.querySelectorAll(
                            `#promo-slider-image-${sliderId} .swiper-slide a`,
                        )
                        allSlideLinks.forEach((link) => {
                            link.setAttribute('tabindex', '-1')
                        })

                        // Then set only the active slide's link to 0
                        const activeSlide = swiper.slides[swiper.activeIndex]
                        if (activeSlide) {
                            const activeLink = activeSlide.querySelector('a')
                            if (activeLink) {
                                activeLink.setAttribute('tabindex', '0')
                            }
                        }

                        if (navigationDirection.current) {
                            // Use requestAnimationFrame for smoother focus transition
                            const direction = navigationDirection.current
                            requestAnimationFrame(() => {
                                setTimeout(() => {
                                    const targetSlide = swiper.slides[swiper.activeIndex]
                                    if (targetSlide) {
                                        const focusableElement = targetSlide.querySelector('a')
                                        if (focusableElement) {
                                            focusableElement.focus({preventScroll: true})
                                        }
                                    }
                                    // Only clear if this is still the same direction
                                    if (navigationDirection.current === direction) {
                                        navigationDirection.current = null
                                    }
                                }, 100)
                            })
                        }
                    }}
                    breakpoints={{
                        0: {
                            slidesPerView: slidesToView.base,
                            spaceBetween: 16,
                            centeredSlides: true,
                        },
                        768: {
                            slidesPerView: slidesToView.md,
                            spaceBetween: 16,
                            centeredSlides: true,
                        },
                        1024: {
                            slidesPerView: slidesToView.lg,
                            spaceBetween: 16,
                            centeredSlides: true,
                        },
                        1200: {
                            slidesPerView: slidesToView.xl,
                            spaceBetween: 16,
                            centeredSlides: true,
                        },
                        1400: {
                            slidesPerView: slidesToView.xxl,
                            spaceBetween: 16,
                            centeredSlides: true,
                        },
                    }}
                >
                    {allSlides.map((slider, index) => (
                        <SwiperSlide key={index} data-fake={slider.isFake ? 'true' : 'false'}>
                            <PromoSlideImageCard
                                title={slider.text_block.title}
                                bodyCopy={slider.text_block.body_copy}
                                button={slider.text_block.button}
                                url={slider?.media?.url}
                                alt={slider?.media?.description}
                                images={slider.images}
                                tabIndex={-1}
                            />
                        </SwiperSlide>
                    ))}
                    <Box
                        className="promo-slider-pagination"
                        role="group"
                        aria-label={intl.formatMessage({
                            id: 'label.promo.swiper.pagination',
                            defaultMessage: `Pagination for promo slider`,
                        })}
                    />
                </Swiper>
                <HideOnMobile>
                    <Box
                        ref={nextButtonRef}
                        className={`swiper-button-next swiper-button-next-${sliderId}`}
                        aria-label={intl.formatMessage({
                            id: 'label.promo.swiper.button.next',
                            defaultMessage: 'swiper button next icon',
                        })}
                        tabIndex={0}
                        role="button"
                        onClick={() => {
                            navigationDirection.current = 'next'
                            swiperRef.current?.slideNext()
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault()
                                e.stopPropagation()
                                navigationDirection.current = 'next'
                                if (swiperRef.current) {
                                    swiperRef.current.slideNext()
                                }
                            }
                        }}
                        {...styles.heroLeftRightArrowsContainer}
                        top={{base: '50%', md: 'calc(50% - 60px)', lg: '50%'}}
                        right={{base: '4%', md: '18%', lg: '22%', xl: '26%'}}
                    >
                        <PromoSliderIcons.right
                            {...styles.heroLeftRightArrows}
                            boxSize={remCalc(16)}
                        />
                    </Box>
                </HideOnMobile>
            </Box>
        </Box>
    )
}

PromoSliderImage.propTypes = {
    heading: PropTypes.string,
    uid: PropTypes.string,
    sliderGroup: PropTypes.arrayOf(
        PropTypes.shape({
            text_block: PropTypes.shape({
                title: PropTypes.string,
                body_copy: PropTypes.string,
                cta: PropTypes.shape({title: PropTypes.string, href: PropTypes.string}),
                is_external_url: PropTypes.bool,
            }),
            media: PropTypes.shape({
                url: PropTypes.string,
                alt: PropTypes.string,
                description: PropTypes.string,
            }),
        }),
    ),
}
